import { io, type Socket } from 'socket.io-client';
import type { CallSignal, ConversationStatus, WidgetMessage } from './types';

export type CallEvent =
  | { type: 'accepted'; callId: string; agentName: string | null }
  | { type: 'ended'; callId: string; reason: 'missed' | 'ended' }
  | { type: 'signal'; callId: string; signal: CallSignal };

export interface RealtimeHandlers {
  onMessage(message: WidgetMessage): void;
  onStatus(status: ConversationStatus, agentName: string | null): void;
  onTyping(name: string | null, typing: boolean): void;
  /** Browser voice calls with the team. */
  onCall?(event: CallEvent): void;
}

export interface WidgetRealtime {
  typing(active: boolean): void;
  /** This connection's id: a call started from it is missed if it drops while ringing. */
  socketId(): string | null;
  callSignal(callId: string, signal: CallSignal): void;
  close(): void;
}

/**
 * Live updates from the team (agent replies, takeover, typing). Opened only after the visitor
 * first opens the chat, so page loads stay light (PRD §14).
 */
export function connectRealtime(
  apiUrl: string,
  token: string,
  handlers: RealtimeHandlers,
): WidgetRealtime {
  const socket: Socket = io(`${apiUrl.replace(/\/+$/, '')}/widget`, {
    auth: { token },
    transports: ['websocket', 'polling'],
  });
  socket.on('message.created', (p: { message: WidgetMessage }) => handlers.onMessage(p.message));
  socket.on(
    'conversation.status_changed',
    (p: { status: ConversationStatus; agentName: string | null }) =>
      handlers.onStatus(p.status, p.agentName),
  );
  socket.on('typing.started', (p: { name: string | null }) => handlers.onTyping(p.name, true));
  socket.on('typing.stopped', () => handlers.onTyping(null, false));
  socket.on('call.accepted', (p: { callId: string; agentName: string | null }) =>
    handlers.onCall?.({ type: 'accepted', ...p }),
  );
  socket.on('call.ended', (p: { callId: string; reason: 'missed' | 'ended' }) =>
    handlers.onCall?.({ type: 'ended', ...p }),
  );
  socket.on('call.signal', (p: { callId: string; signal: CallSignal }) =>
    handlers.onCall?.({ type: 'signal', ...p }),
  );
  // A refused handshake (bad token, site not allowed) should not retry forever.
  socket.on('auth_error', () => socket.disconnect());

  let last = false;
  return {
    typing(active) {
      if (active === last) return;
      last = active;
      socket.emit('typing', { typing: active });
    },
    socketId() {
      return socket.id ?? null;
    },
    callSignal(callId, signal) {
      socket.emit('call.signal', { callId, signal });
    },
    close() {
      socket.disconnect();
    },
  };
}
